import React from 'react'

import { Box, Table } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { ExecutionHistoryEntriesCollection } from 'modules/automations/store/collections/executionHistoryEntries'
import type { Automation } from 'modules/automations/store/models/Automation'
import CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'

import { COLUMN_SPANS, TOTAL_COLUMNS_SPAN } from './constants'
import HistoryEntryRow from './entry_row'

interface HistoryTableProps {
  automation: Automation
}

const HistoryEntriesTable = ({ automation }: HistoryTableProps) => {
  const executionHistoryEntries = React.useRef(new ExecutionHistoryEntriesCollection())
  const [hasMoreRegisters, setHasMoreRegisters] = React.useState(true)

  const { running: loading, performAction: loadMore } = useAsyncAction(
    async () => {
      const entries = await executionHistoryEntries.current.fetchPaginated(automation)

      if (entries.length < 10) {
        setHasMoreRegisters(false)
      }

      return entries
    },
    { successFlash: false }
  )

  useEffect(() => {
    executionHistoryEntries.current = new ExecutionHistoryEntriesCollection()
    setHasMoreRegisters(true)

    loadMore()
  }, [automation.id])

  const customFieldsCollection = useMemo(
    () =>
      new CustomFieldsCollection([], {
        model_ref: '',
        instance_id: '',
      }),
    []
  )

  useAsyncData(() => customFieldsCollection.byAutomationsUsageGroup(), [])

  const handleLoadMore = useCallback(async () => {
    if (!loading && hasMoreRegisters) {
      await loadMore()
    }
  }, [loading])

  const columns = [
    {
      name: i18n.t('automations.history.table.headers.status'),
      colSpan: COLUMN_SPANS[0],
    },
    {
      name: i18n.t('automations.history.table.headers.executed_at'),
      colSpan: COLUMN_SPANS[1],
    },
    {
      name: '',
      colSpan: COLUMN_SPANS[2],
    },
  ]

  return (
    <Box>
      <Table>
        <Table.Head>
          <Table.Row>
            {columns.map((column) => (
              <Table.HeadCell colSpan={column.colSpan} key={column.name}>
                {column.name}
              </Table.HeadCell>
            ))}
          </Table.Row>
        </Table.Head>
        {!loading && !hasMoreRegisters && executionHistoryEntries.current.length === 0 && (
          <Table.Body>
            <Table.Blank
              colSpan={TOTAL_COLUMNS_SPAN}
              text={i18n.t('automations.history.table.no_executions_found')}
            />
          </Table.Body>
        )}
        {executionHistoryEntries.current.length > 0 && (
          <Table.BodyWithInfiniteScroll
            page={0} // No pages here
            hasMore={hasMoreRegisters}
            loadMore={handleLoadMore}
            colSpan={TOTAL_COLUMNS_SPAN}
          >
            {executionHistoryEntries.current.map((entry) => (
              <HistoryEntryRow
                key={entry.id}
                historyEntry={entry}
                customFieldsCollection={customFieldsCollection}
              />
            ))}
          </Table.BodyWithInfiniteScroll>
        )}
        {loading && <Table.Loading colSpan={TOTAL_COLUMNS_SPAN} />}
      </Table>
    </Box>
  )
}

export default observer(HistoryEntriesTable)
